<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  getComplianceDashboard,
  getFatigueOverview,
  getFatiguePolicy,
  listProjects,
  listWorkers,
  saveFatiguePolicy,
  type FatiguePolicy,
  type WorkerFatigue,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import WorkerAvatar from "@/components/WorkerAvatar.vue";

const queryClient = useQueryClient();
const weeks = ref(3);
const { data, isPending, isError, error } = useQuery({
  queryKey: ["fatigue-overview", weeks],
  queryFn: () => getFatigueOverview(weeks.value),
});

// Org-wide context for the headline cards (graceful — cards fall back to the
// roster window if these don't load).
const { data: compliance } = useQuery({
  queryKey: ["compliance-dashboard"],
  queryFn: () => getComplianceDashboard(),
});
const { data: projects } = useQuery({
  queryKey: ["projects"],
  queryFn: () => listProjects(),
});
// Worker photos (worker_id / ncca_no -> image file). Avatars fall back to
// initials when a worker isn't matched or has no photo.
const { data: workerRows } = useQuery({
  queryKey: ["workers", true],
  queryFn: () => listWorkers(true),
});
const imageByKey = computed(() => {
  const m = new Map<string, string | null>();
  for (const r of workerRows.value ?? []) {
    if (r.id) m.set(r.id, r.image_file_id);
    if (r.ncca_no) m.set(r.ncca_no, r.image_file_id);
  }
  return m;
});
function avatarFor(w: WorkerFatigue): string | null {
  return imageByKey.value.get(w.worker_id ?? "") ?? imageByKey.value.get(w.ncca_no) ?? null;
}

// Configurable working-time limits (management).
const { data: policy } = useQuery({
  queryKey: ["fatigue-policy"],
  queryFn: () => getFatiguePolicy(),
});
const editingLimits = ref(false);
const limitsBusy = ref(false);
const draft = reactive<FatiguePolicy>({
  max_shift_hours: 12,
  min_rest_hours: 11,
  max_weekly_hours: 48,
  max_consecutive_days: 6,
  max_consecutive_nights: 4,
  night_start_hour: 22,
  night_end_hour: 6,
  commute_minutes: 40,
  workload: "moderate",
  attention: "some",
  longest_spell_minutes: 240,
  break_minutes: 30,
});
const LIMIT_FIELDS: { key: keyof FatiguePolicy; label: string }[] = [
  { key: "max_shift_hours", label: "Max shift (h)" },
  { key: "min_rest_hours", label: "Min rest (h)" },
  { key: "max_weekly_hours", label: "Max 7-day (h)" },
  { key: "max_consecutive_days", label: "Max consec. days" },
  { key: "max_consecutive_nights", label: "Max consec. nights" },
  { key: "night_start_hour", label: "Night start (h)" },
  { key: "night_end_hour", label: "Night end (h)" },
];
const INPUT_NUM_FIELDS: { key: keyof FatiguePolicy; label: string }[] = [
  { key: "commute_minutes", label: "Commute (min)" },
  { key: "longest_spell_minutes", label: "Longest spell (min)" },
  { key: "break_minutes", label: "Break length (min)" },
];
const WORKLOAD_OPTIONS = ["low", "moderate", "high", "very_high"];
const ATTENTION_OPTIONS = ["rarely", "some", "most", "continuous"];
function startEditLimits(): void {
  if (policy.value) Object.assign(draft, policy.value);
  editingLimits.value = true;
}
async function saveLimits(): Promise<void> {
  limitsBusy.value = true;
  try {
    await saveFatiguePolicy({ ...draft });
    await queryClient.invalidateQueries({ queryKey: ["fatigue-policy"] });
    await queryClient.invalidateQueries({ queryKey: ["fatigue-overview"] });
    editingLimits.value = false;
  } finally {
    limitsBusy.value = false;
  }
}

// Fatigue Index (0..100, % high KSS): ~20 is an average rotating schedule.
function fiClass(score: number): { label: string; cls: string } {
  if (score >= 45) return { label: "High", cls: "bg-semantic-danger/10 text-semantic-danger" };
  if (score >= 25) return { label: "Elevated", cls: "bg-semantic-warning/10 text-semantic-warning" };
  return { label: "Low", cls: "bg-semantic-success/10 text-semantic-success" };
}
// Risk Index (~1.0 = average accident risk for the reference schedule).
function riClass(score: number): { label: string; cls: string } {
  if (score >= 1.6) return { label: "High", cls: "bg-semantic-danger/10 text-semantic-danger" };
  if (score >= 1.2) return { label: "Elevated", cls: "bg-semantic-warning/10 text-semantic-warning" };
  return { label: "Low", cls: "bg-semantic-success/10 text-semantic-success" };
}

const workers = computed<WorkerFatigue[]>(() => data.value?.workers ?? []);
const pol = computed<Record<string, number>>(() => data.value?.policy ?? {});

// --- Headline cards ---
const totalWorkers = computed(
  () => compliance.value?.stats.active_workers ?? workers.value.length,
);
const rostered = computed(() => workers.value.length);
const activeProjects = computed(() => (projects.value ?? []).length);
const totalAlerts = computed(() =>
  workers.value.reduce((n, w) => n + w.alerts.length, 0),
);

// --- Worker compliance: breach => non-compliant; high score => warning ---
function workerState(w: WorkerFatigue): "non" | "warn" | "ok" {
  if (w.alerts.length) return "non";
  if (w.max_fatigue_index >= 45 || w.max_risk_index >= 1.6) return "warn";
  return "ok";
}
const compliantCount = computed(
  () => workers.value.filter((w) => workerState(w) === "ok").length,
);
const warningCount = computed(
  () => workers.value.filter((w) => workerState(w) === "warn").length,
);
const nonCompliantCount = computed(
  () => workers.value.filter((w) => workerState(w) === "non").length,
);
function pct(n: number): string {
  const t = rostered.value || 1;
  return `${(n / t) * 100}%`;
}

// --- Fatigue-risk distribution (by Fatigue Index band) ---
const DIST_ROWS = [
  { key: "low", label: "Low", cls: "bg-semantic-success" },
  { key: "medium", label: "Medium", cls: "bg-semantic-warning" },
  { key: "high", label: "High", cls: "bg-semantic-danger/70" },
  { key: "critical", label: "Critical", cls: "bg-semantic-danger" },
] as const;
const distribution = computed<Record<string, number>>(() => {
  const b = { low: 0, medium: 0, high: 0, critical: 0 };
  for (const w of workers.value) {
    const fi = w.max_fatigue_index;
    if (fi >= 60) b.critical++;
    else if (fi >= 45) b.high++;
    else if (fi >= 25) b.medium++;
    else b.low++;
  }
  return b;
});
const distMax = computed(() => Math.max(1, ...Object.values(distribution.value)));

// --- Regulation alerts (worst first) + workers needing attention ---
const byFatigueDesc = computed(() =>
  [...workers.value].sort((a, b) => b.max_fatigue_index - a.max_fatigue_index),
);
const alertWorkers = computed(() =>
  byFatigueDesc.value.filter((w) => w.alerts.length),
);
const attention = computed(() =>
  byFatigueDesc.value
    .filter((w) => w.alerts.length || w.max_fatigue_index >= 25)
    .slice(0, 6),
);

// --- Working-time reference cards (the tenant's own editable limits) ---
function fmtHr(h: number | undefined): string {
  return h == null ? "—" : `${String(h).padStart(2, "0")}:00`;
}
const wtrCards = computed(() => {
  const p = pol.value;
  return [
    { label: "Max shift", value: `${p.max_shift_hours ?? "—"} h`, note: "Maximum shift length" },
    { label: "Max 7-day hours", value: `${p.max_weekly_hours ?? "—"} h`, note: "Rolling 7-day window" },
    { label: "Daily rest", value: `${p.min_rest_hours ?? "—"} h`, note: "Minimum between shifts" },
    { label: "Consecutive days", value: `${p.max_consecutive_days ?? "—"}`, note: "Max day shifts in a row" },
    { label: "Consecutive nights", value: `${p.max_consecutive_nights ?? "—"}`, note: "Max night shifts in a row" },
    { label: "Night window", value: `${fmtHr(p.night_start_hour)}–${fmtHr(p.night_end_hour)}`, note: "Counts as night work" },
  ];
});

const expanded = ref<Set<string>>(new Set());
function toggle(ncca: string): void {
  const next = new Set(expanded.value);
  if (next.has(ncca)) next.delete(ncca);
  else next.add(ncca);
  expanded.value = next;
}
function alertText(w: WorkerFatigue): string {
  return w.alerts.length
    ? `${w.alerts.length} alert${w.alerts.length === 1 ? "" : "s"}`
    : "—";
}

const tableEl = ref<HTMLElement | null>(null);
function scrollToTable(): void {
  tableEl.value?.scrollIntoView({ behavior: "smooth", block: "start" });
}
</script>

<template>
  <PageLayout wide>
    <div class="mb-1 flex flex-wrap items-center gap-3">
      <h1 class="text-xl font-semibold">
        Fatigue &amp; risk
      </h1>
      <button
        type="button"
        class="ml-auto rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
        @click="editingLimits ? (editingLimits = false) : startEditLimits()"
      >
        {{ editingLimits ? "Close" : "Edit limits" }}
      </button>
      <label class="flex items-center gap-2 text-sm text-neutral-500">
        Window
        <select
          v-model.number="weeks"
          class="rounded-md border border-neutral-300 px-2 py-1 text-sm"
        >
          <option
            v-for="n in [1, 2, 3, 4, 6]"
            :key="n"
            :value="n"
          >
            {{ n }} week{{ n === 1 ? "" : "s" }}
          </option>
        </select>
      </label>
    </div>
    <p class="mb-4 text-sm text-neutral-500">
      The HSE Fatigue &amp; Risk Index (Research Report 446) computed from the
      roster. The <strong>Fatigue Index</strong> is the % likelihood of high
      sleepiness (≈20 is an average rotating pattern); the
      <strong>Risk Index</strong> is relative accident risk (1.0 = average).
      Criteria alerts flag working-time breaches.
    </p>

    <!-- Editable working-time limits -->
    <div
      v-if="editingLimits"
      class="mb-4 rounded-xl border border-neutral-200 bg-neutral-50 p-4"
    >
      <p class="mb-3 text-sm font-medium">
        Working-time limits
        <span class="font-normal text-neutral-400">— what the Criteria Alerts are judged against</span>
      </p>
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <label
          v-for="f in LIMIT_FIELDS"
          :key="f.key"
          class="block"
        >
          <span class="mb-1 block text-xs text-neutral-500">{{ f.label }}</span>
          <input
            v-model.number="draft[f.key]"
            type="number"
            min="0"
            class="w-full rounded-md border border-neutral-300 px-2 py-1 text-sm"
          >
        </label>
      </div>

      <p class="mb-2 mt-4 text-sm font-medium">
        Duty inputs
        <span class="font-normal text-neutral-400">— your operation's commute, work intensity and breaks (feed the score, not the alerts)</span>
      </p>
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Workload</span>
          <select
            v-model="draft.workload"
            class="w-full rounded-md border border-neutral-300 px-2 py-1 text-sm"
          >
            <option
              v-for="w in WORKLOAD_OPTIONS"
              :key="w"
              :value="w"
            >
              {{ w.replace("_", " ") }}
            </option>
          </select>
        </label>
        <label class="block">
          <span class="mb-1 block text-xs text-neutral-500">Attention</span>
          <select
            v-model="draft.attention"
            class="w-full rounded-md border border-neutral-300 px-2 py-1 text-sm"
          >
            <option
              v-for="a in ATTENTION_OPTIONS"
              :key="a"
              :value="a"
            >
              {{ a }}
            </option>
          </select>
        </label>
        <label
          v-for="f in INPUT_NUM_FIELDS"
          :key="f.key"
          class="block"
        >
          <span class="mb-1 block text-xs text-neutral-500">{{ f.label }}</span>
          <input
            v-model.number="draft[f.key]"
            type="number"
            min="0"
            class="w-full rounded-md border border-neutral-300 px-2 py-1 text-sm"
          >
        </label>
      </div>

      <div class="mt-3 flex items-center gap-2">
        <button
          type="button"
          class="rounded-md bg-brand-primary px-4 py-1.5 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
          :disabled="limitsBusy"
          @click="saveLimits"
        >
          {{ limitsBusy ? "Saving…" : "Save limits" }}
        </button>
        <button
          type="button"
          class="text-sm text-neutral-500 hover:text-neutral-900"
          @click="editingLimits = false"
        >
          Cancel
        </button>
      </div>
    </div>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div v-else-if="data">
      <!-- Headline cards -->
      <div class="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Total workers
          </p>
          <p class="mt-1 text-2xl font-semibold">
            {{ totalWorkers }}
          </p>
          <p class="text-xs text-neutral-400">
            Active in system
          </p>
        </div>
        <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Rostered
          </p>
          <p class="mt-1 text-2xl font-semibold">
            {{ rostered }}
          </p>
          <p class="text-xs text-neutral-400">
            With shifts in the {{ weeks }}-week window
          </p>
        </div>
        <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Open alerts
          </p>
          <p
            class="mt-1 text-2xl font-semibold"
            :class="totalAlerts ? 'text-semantic-danger' : ''"
          >
            {{ totalAlerts }}
          </p>
          <p class="text-xs text-neutral-400">
            Working-time breaches
          </p>
        </div>
        <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Active projects
          </p>
          <p class="mt-1 text-2xl font-semibold">
            {{ activeProjects }}
          </p>
          <p class="text-xs text-neutral-400">
            Across the org
          </p>
        </div>
      </div>

      <p
        v-if="!workers.length"
        class="rounded-xl border border-neutral-200 bg-neutral-0 p-6 text-sm text-neutral-500"
      >
        No rostered shifts in the selected window. Import a roster to see fatigue
        scores.
      </p>

      <template v-else>
        <div class="grid gap-4 lg:grid-cols-2">
          <!-- Worker compliance overview -->
          <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
            <div class="mb-3 flex items-center justify-between">
              <h2 class="text-sm font-semibold">
                Worker compliance overview
              </h2>
              <button
                type="button"
                class="text-xs text-brand-primary hover:underline"
                @click="scrollToTable"
              >
                View all workers ↓
              </button>
            </div>
            <div class="mb-3 flex items-center justify-around text-center">
              <div>
                <p class="text-2xl font-semibold text-semantic-success">
                  {{ compliantCount }}
                </p>
                <p class="text-xs text-neutral-500">
                  Compliant
                </p>
              </div>
              <div>
                <p class="text-2xl font-semibold text-semantic-warning">
                  {{ warningCount }}
                </p>
                <p class="text-xs text-neutral-500">
                  Warning
                </p>
              </div>
              <div>
                <p class="text-2xl font-semibold text-semantic-danger">
                  {{ nonCompliantCount }}
                </p>
                <p class="text-xs text-neutral-500">
                  Non-compliant
                </p>
              </div>
            </div>
            <div class="flex h-2.5 overflow-hidden rounded-full bg-neutral-100">
              <div
                class="bg-semantic-success"
                :style="{ width: pct(compliantCount) }"
              />
              <div
                class="bg-semantic-warning"
                :style="{ width: pct(warningCount) }"
              />
              <div
                class="bg-semantic-danger"
                :style="{ width: pct(nonCompliantCount) }"
              />
            </div>
            <ul class="mt-3 divide-y divide-neutral-100">
              <li
                v-for="w in byFatigueDesc.slice(0, 5)"
                :key="w.ncca_no"
                class="flex items-center justify-between gap-2 py-1.5 text-sm"
              >
                <span class="flex min-w-0 items-center gap-2">
                  <WorkerAvatar
                    :name="w.name"
                    :file-id="avatarFor(w)"
                    :size="28"
                  />
                  <RouterLink
                    v-if="w.worker_id"
                    :to="`/workers/${w.worker_id}`"
                    class="truncate font-medium text-brand-primary hover:underline"
                  >
                    {{ w.name }}
                  </RouterLink>
                  <span
                    v-else
                    class="truncate font-medium"
                  >{{ w.name }}</span>
                </span>
                <span
                  class="shrink-0 rounded-full px-2 py-0.5 text-xs"
                  :class="fiClass(w.max_fatigue_index).cls"
                >
                  {{ w.max_fatigue_index.toFixed(0) }} · {{ fiClass(w.max_fatigue_index).label }}
                </span>
              </li>
            </ul>
          </section>

          <!-- Regulation alerts -->
          <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
            <div class="mb-3 flex items-center justify-between">
              <h2 class="text-sm font-semibold">
                Regulation alerts
              </h2>
              <span class="flex items-center gap-2 text-xs">
                <span class="rounded-full bg-semantic-danger/10 px-2 py-0.5 text-semantic-danger">
                  {{ nonCompliantCount }} critical
                </span>
                <span class="rounded-full bg-semantic-warning/10 px-2 py-0.5 text-semantic-warning">
                  {{ warningCount }} warning
                </span>
              </span>
            </div>
            <p
              v-if="!alertWorkers.length"
              class="py-6 text-center text-sm text-neutral-400"
            >
              No working-time breaches in this window. 🎉
            </p>
            <ul
              v-else
              class="space-y-2"
            >
              <li
                v-for="w in alertWorkers.slice(0, 6)"
                :key="w.ncca_no"
                class="rounded-lg border-l-4 border-semantic-danger bg-semantic-danger/5 px-3 py-2"
              >
                <div class="flex items-center justify-between gap-2">
                  <span class="flex min-w-0 items-center gap-2">
                    <WorkerAvatar
                      :name="w.name"
                      :file-id="avatarFor(w)"
                      :size="28"
                    />
                    <RouterLink
                      v-if="w.worker_id"
                      :to="`/workers/${w.worker_id}`"
                      class="truncate text-sm font-medium text-brand-primary hover:underline"
                    >
                      {{ w.name }}
                    </RouterLink>
                    <span
                      v-else
                      class="truncate text-sm font-medium"
                    >{{ w.name }}</span>
                  </span>
                  <span class="shrink-0 text-xs text-neutral-400">
                    FI {{ w.max_fatigue_index.toFixed(0) }}
                  </span>
                </div>
                <p class="text-xs text-neutral-600">
                  {{ w.alerts[0].message }}
                </p>
              </li>
            </ul>
            <button
              v-if="alertWorkers.length > 6"
              type="button"
              class="mt-3 w-full text-center text-xs text-brand-primary hover:underline"
              @click="scrollToTable"
            >
              View all {{ alertWorkers.length }} flagged workers ↓
            </button>
          </section>

          <!-- Fatigue risk distribution -->
          <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
            <h2 class="mb-3 text-sm font-semibold">
              Fatigue risk distribution
              <span class="font-normal text-neutral-400">— by Fatigue Index</span>
            </h2>
            <div class="space-y-2">
              <div
                v-for="row in DIST_ROWS"
                :key="row.key"
                class="flex items-center gap-3 text-sm"
              >
                <span class="w-16 text-neutral-500">{{ row.label }}</span>
                <div class="h-3 flex-1 overflow-hidden rounded-full bg-neutral-100">
                  <div
                    class="h-full rounded-full"
                    :class="row.cls"
                    :style="{ width: `${(distribution[row.key] / distMax) * 100}%` }"
                  />
                </div>
                <span class="w-8 text-right font-medium">{{ distribution[row.key] }}</span>
              </div>
            </div>
            <p class="mt-2 text-xs text-neutral-400">
              Low &lt;25 · Medium 25–44 · High 45–59 · Critical ≥60
            </p>
          </section>

          <!-- Workers requiring attention -->
          <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
            <h2 class="mb-3 text-sm font-semibold">
              Workers requiring attention
            </h2>
            <p
              v-if="!attention.length"
              class="py-6 text-center text-sm text-neutral-400"
            >
              No elevated fatigue scores in this window.
            </p>
            <ul
              v-else
              class="divide-y divide-neutral-100"
            >
              <li
                v-for="w in attention"
                :key="w.ncca_no"
                class="flex items-center justify-between gap-2 py-2 text-sm"
              >
                <div class="flex min-w-0 items-center gap-2">
                  <WorkerAvatar
                    :name="w.name"
                    :file-id="avatarFor(w)"
                    :size="28"
                  />
                  <div class="min-w-0">
                    <RouterLink
                      v-if="w.worker_id"
                      :to="`/workers/${w.worker_id}`"
                      class="font-medium text-brand-primary hover:underline"
                    >
                      {{ w.name }}
                    </RouterLink>
                    <span
                      v-else
                      class="font-medium"
                    >{{ w.name }}</span>
                    <span class="text-neutral-400">
                      · {{ w.duties }} dut{{ w.duties === 1 ? "y" : "ies" }}
                      <template v-if="w.alerts.length"> · {{ w.alerts.length }} alert{{ w.alerts.length === 1 ? "" : "s" }}</template>
                    </span>
                  </div>
                </div>
                <span
                  class="rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="fiClass(w.max_fatigue_index).cls"
                >
                  {{ w.max_fatigue_index.toFixed(0) }}/100
                </span>
              </li>
            </ul>
          </section>
        </div>

        <!-- Working-time regulations reference -->
        <section class="mt-4 rounded-xl border border-neutral-200 bg-neutral-0 p-4">
          <h2 class="mb-3 text-sm font-semibold">
            Working-time limits reference
            <span class="font-normal text-neutral-400">— your configured policy</span>
          </h2>
          <div class="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <div
              v-for="c in wtrCards"
              :key="c.label"
              class="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-center"
            >
              <p class="text-xs text-neutral-500">
                {{ c.label }}
              </p>
              <p class="my-0.5 text-lg font-semibold">
                {{ c.value }}
              </p>
              <p class="text-xs text-neutral-400">
                {{ c.note }}
              </p>
            </div>
          </div>
        </section>

        <!-- All rostered workers (the detailed worst-first table) -->
        <section
          ref="tableEl"
          class="mt-4"
        >
          <p class="mb-2 text-sm font-semibold">
            All rostered workers
            <span class="font-normal text-neutral-400">
              — weeks {{ data.weeks.join(", ") }} ·
              {{ totalAlerts }} alert{{ totalAlerts === 1 ? "" : "s" }} across
              {{ rostered }} workers
            </span>
          </p>
          <div class="overflow-hidden rounded-xl border border-neutral-200 bg-neutral-0">
            <table class="w-full text-left text-sm">
              <thead class="bg-neutral-50 text-neutral-500">
                <tr>
                  <th class="px-4 py-2 font-medium">
                    Worker
                  </th>
                  <th class="px-4 py-2 font-medium">
                    Duties
                  </th>
                  <th class="px-4 py-2 font-medium">
                    Fatigue Index
                  </th>
                  <th class="px-4 py-2 font-medium">
                    Risk Index
                  </th>
                  <th class="px-4 py-2 font-medium">
                    Peak run
                  </th>
                  <th class="px-4 py-2 font-medium">
                    Criteria alerts
                  </th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="w in data.workers"
                  :key="w.ncca_no"
                >
                  <tr
                    class="border-t border-neutral-100"
                    :class="{ 'cursor-pointer hover:bg-neutral-50': w.alerts.length }"
                    @click="w.alerts.length && toggle(w.ncca_no)"
                  >
                    <td class="px-4 py-2">
                      <span class="flex items-center gap-2">
                        <WorkerAvatar
                          :name="w.name"
                          :file-id="avatarFor(w)"
                          :size="32"
                        />
                        <span>
                          <RouterLink
                            v-if="w.worker_id"
                            :to="`/workers/${w.worker_id}`"
                            class="font-medium text-brand-primary hover:underline"
                            @click.stop
                          >
                            {{ w.name }}
                          </RouterLink>
                          <span
                            v-else
                            class="font-medium"
                          >{{ w.name }}</span>
                          <span class="ml-1 text-neutral-400">{{ w.ncca_no }}</span>
                        </span>
                      </span>
                    </td>
                    <td class="px-4 py-2 text-neutral-500">
                      {{ w.duties }}
                    </td>
                    <td class="px-4 py-2">
                      <span class="font-medium">{{ w.max_fatigue_index.toFixed(0) }}</span>
                      <span
                        class="ml-1.5 rounded-full px-2 py-0.5 text-xs"
                        :class="fiClass(w.max_fatigue_index).cls"
                      >{{ fiClass(w.max_fatigue_index).label }}</span>
                    </td>
                    <td class="px-4 py-2">
                      <span class="font-medium">{{ w.max_risk_index.toFixed(2) }}</span>
                      <span
                        class="ml-1.5 rounded-full px-2 py-0.5 text-xs"
                        :class="riClass(w.max_risk_index).cls"
                      >{{ riClass(w.max_risk_index).label }}</span>
                    </td>
                    <td class="px-4 py-2 text-neutral-500">
                      {{ w.peak_consecutive }}
                    </td>
                    <td class="px-4 py-2">
                      <span
                        v-if="w.alerts.length"
                        class="rounded-full bg-semantic-warning/10 px-2 py-0.5 text-xs text-semantic-warning"
                      >{{ alertText(w) }} ▾</span>
                      <span
                        v-else
                        class="text-neutral-300"
                      >—</span>
                    </td>
                  </tr>
                  <tr
                    v-if="w.alerts.length && expanded.has(w.ncca_no)"
                    class="border-t border-neutral-100 bg-neutral-50"
                  >
                    <td
                      colspan="6"
                      class="px-4 py-2"
                    >
                      <ul class="space-y-1">
                        <li
                          v-for="(a, i) in w.alerts"
                          :key="i"
                          class="text-xs text-neutral-600"
                        >
                          <span class="text-neutral-400">{{ a.date }}</span>
                          — {{ a.message }}
                        </li>
                      </ul>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </section>
      </template>
    </div>
  </PageLayout>
</template>
